import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, ReceiptIcon, ActivityIcon, LandmarkIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { timeZoneAbbr } from '../../../shared/config/localeProfile';
import { BRAND } from '../../../shared/config/brand';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { getPayoutById, getAccountDetails, getDisplayStatus, getPayoutTraceId, getInitiatedAt, getPayoutCalcSections, type Payout, type DisplayStatus } from '../mocks/payoutsData';
// ── Formatting helpers ────────────────────────────────────────────────────
// All dates on this page are rendered in the tenant timezone (MVT = UTC+5)
// with the timezone explicit so merchants can reconcile against bank
// statements without having to guess.
const fmtAmount = (n: number) => formatCurrencyAmount(Math.abs(n), {
  fractionDigits: 2
});
const fmtSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtAmount(n)}`;
// Full date + time with explicit MVT timezone, for the header and any
// "this is *the* moment that happened" context.
const fmtDateTimeMVT = (d: Date) => {
  const s = d.toLocaleString('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${s} ${timeZoneAbbr()}`;
};
// Used inside expandable cards (e.g. account "Created") where the
// timezone is implicit from page context.
const fmtDateTime = (d: Date) => d.toLocaleString('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});
// ── Status pill ──────────────────────────────────────────────────────────
// "Sent" rather than "Paid" — we know we handed the payout to the bank,
// we don't know that the destination account has been credited. Blue
// (rather than green) signals "in motion, not yet final from your side".
const STATUS_LABEL: Record<DisplayStatus, string> = {
  paid: 'Sent',
  scheduled: 'Scheduled',
  partially_submitted: 'Partially submitted',
  failed: 'Failed',
  cancelled: 'Cancelled'
};
const STATUS_PILL: Record<DisplayStatus, string> = {
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduled: 'bg-gray-50 text-gray-700 border-gray-200',
  partially_submitted: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-600 border-gray-200'
};
function StatusPill({
  status
}: {
  status: DisplayStatus;
}) {
  return <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full ${STATUS_PILL[status]}`} data-id="element-3101" data-mp="30rxmhn6m2sil">
      {STATUS_LABEL[status]}
    </span>;
}
// ── Section primitive ────────────────────────────────────────────────────
function Section({
  icon: Icon,
  title,
  description,
  children,
  action
}: {
  icon?: ComponentType<{
    size?: number;
    className?: string;
  }>;
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-lg" data-id="element-3102" data-mp="26xexobn9h4tk">
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200" data-id="element-3103" data-mp="2byb714qdtj6d">
        <div className="min-w-0" data-id="element-3104" data-mp="1xz1tydxh356m">
          <div className="flex items-center gap-2" data-id="element-3105" data-mp="1xzuv8zdnx2hd">
            {Icon && <Icon size={15} className="text-gray-500" aria-hidden="true" data-id="element-3106" data-mp="22yeid33zskbb" />}
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-3107" data-mp="3sc1bwdpbsbh2">{title}</h2>
          </div>
          {description && <p className={`text-xs text-gray-500 mt-0.5 ${Icon ? 'ml-[22px]' : ''}`} data-id="element-3108" data-mp="15hugfozhn9s">
              {description}
            </p>}
        </div>
        {action}
      </div>
      {children}
    </section>;
}
// ── Copy button ──────────────────────────────────────────────────────────
function CopyButton({
  value,
  label,
  size = 14
}: {
  value: string;
  label: string;
  size?: number;
}) {
  const [copied, setCopied] = useState(false);
  return <button type="button" onClick={async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }} className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors" aria-label={`Copy ${label}`} data-id="element-3109" data-mp="1eb28bkes4rei">
      {copied ? <CheckIcon size={size} className="text-emerald-600" aria-hidden="true" data-id="element-3110" data-mp="vlltrt69eyf3" /> : <CopyIcon size={size} aria-hidden="true" data-id="element-3111" data-mp="328kt776fikcz" />}
    </button>;
}
// ── Account details (expandable) ─────────────────────────────────────────
function AccountDetails({
  payout
}: {
  payout: Payout;
}) {
  const [open, setOpen] = useState(false);
  const a = getAccountDetails(payout);
  return <Section icon={LandmarkIcon} title="Account details" data-id="element-3112" data-mp="3g8ce7rvd510h">
      <div data-id="element-3113" data-mp="3g5q5vq0nehh7">
        <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-gray-50 transition-colors" aria-expanded={open} data-id="element-3114" data-mp="2jhjwyqwp12x4">
          <span className="flex items-center gap-3 min-w-0" data-id="element-3115" data-mp="2sh6z5ex8ycr8">
            {open ? <ChevronDownIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3116" data-mp="2x9hvy8woxifk" /> : <ChevronRightIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3117" data-mp="24sdzb7di7krc" />}
            <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-gray-100 flex-shrink-0" data-id="element-3118" data-mp="3818cs0bxap7t">
              <LandmarkIcon size={16} className="text-gray-600" aria-hidden="true" data-id="element-3119" data-mp="3gvvd4i2qcbd7" />
            </span>
            <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-3120" data-mp="3cv34nr1omahz">
              {a.bank}
            </span>
            <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-3121" data-mp="2p6jxjeudc3rh">
              •••• {a.last4}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap" data-id="element-3122" data-mp="2y5wy1i0ili02">
            <span aria-hidden="true" data-id="element-3123" data-mp="2t5sne8mafhhr">{a.origin.flag}</span>
            {payout.currency}
          </span>
        </button>
        {open && <div className="px-6 pb-5 pt-1 border-t border-gray-100" data-id="element-3124" data-mp="ddbkvfk1x5ie">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 mt-4" data-id="element-3125" data-mp="8f1ox2qi60vq">
              <KV label="Created" data-id="element-3126" data-mp="gwkg7p9dev0x">{fmtDateTime(a.created)}</KV>
              <KV label="Account holder" data-id="element-3127" data-mp="gzgejep4rlpw">{a.accountHolder}</KV>
              <KV label="Routing number" mono data-id="element-3128" data-mp="kzgr0tso5mub">
                {a.routingNumber}
              </KV>
              <KV label="Currency" data-id="element-3129" data-mp="707m4q9wyf5n">{payout.currency}</KV>
            </dl>
          </div>}
      </div>
    </Section>;
}
function KV({
  label,
  children,
  mono,
  copyable
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-start gap-3 min-w-0" data-id="element-3130" data-mp="14lmv4pcme9rq">
      <dt className="text-xs text-gray-500 pt-0.5" data-id="element-3131" data-mp="pya8exb2txzr">{label}</dt>
      <dd className={`flex items-center gap-1 text-sm text-gray-900 min-w-0 ${mono ? 'font-mono' : ''}`} data-id="element-3132" data-mp="pvxrmnh21toa">
        <span className="truncate min-w-0" data-id="element-3133" data-mp="1impir6j9js4r">{children}</span>
        {copyable && <CopyButton value={copyable} label={label} size={12} data-id="element-3134" data-mp="1ikcvqeynerku" />}
      </dd>
    </div>;
}
// ── Payout calculation ─────────────────────────────────────────────────────
// A dynamic, vertically-stacked breakdown. Only-applicable, non-zero signed
// line items render, grouped into a stable, bank/market-agnostic set of
// merchant-facing categories: Payment activity / Fees and taxes / Reserve
// adjustments / Minimum balance adjustments / Account charges / Other
// adjustments. The running list always sums to Net payout, so new adjustment
// kinds appear here automatically — no new columns and no reserved empty rows.
function PayoutCalculationSection({
  payout
}: {
  payout: Payout;
}) {
  const sections = getPayoutCalcSections(payout);
  return <Section icon={ReceiptIcon} title="Payout calculation" data-id="element-3135" data-mp="3tdp1by4qg4pr">
      <div className="px-6 pt-4 pb-5" data-id="element-3136" data-mp="3ofdulw9qiabr">
        <dl className="space-y-5" data-id="element-3137" data-mp="3oec6or7mdtcm">
          {sections.map(section => <div key={section.key} data-id="element-3138" data-mp="2hzuqpwoopphf">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2" data-id="element-3139" data-mp="2i037hznxhexw">
                {section.label}
              </dt>
              <div className="space-y-1.5" data-id="element-3140" data-mp="sifzxhvwy9ri">
                {section.rows.map((row, i) => <div key={`${section.key}-${i}`} className="flex items-center justify-between gap-4 text-sm" data-id="element-3141" data-mp="xhaiqjtvrybm" data-mpi={i}>
                    <span className={row.isGross ? 'text-gray-900 font-medium' : 'text-gray-600'} data-id="element-3142" data-mp="11grnfb1hgrfq">
                      {row.label}
                    </span>
                    <span className={`tabular-nums ${row.isGross ? 'text-gray-900 font-medium' : row.amount < 0 ? 'text-gray-900' : 'text-emerald-700'}`} data-id="element-3143" data-mp="1g9v5djizl76z">
                      {row.isGross ? fmtAmount(row.amount) : fmtSigned(row.amount)}
                    </span>
                  </div>)}
              </div>
            </div>)}
        </dl>

        {/* Totals */}
        <div className="mt-5 pt-4 border-t border-gray-200 space-y-2" data-id="element-3144" data-mp="1i2twqjw4270v">
          <div className="flex items-center justify-between gap-4 text-sm" data-id="element-3145" data-mp="1m3vx541rkp4f">
            <span className="text-gray-600" data-id="element-3146" data-mp="1r27a3nncvfqv">Net adjustments</span>
            <span className="tabular-nums text-gray-900" data-id="element-3147" data-mp="1r40hj55sm3zx">
              {fmtSigned(payout.netAdjustments)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-100" data-id="element-3148" data-mp="210n0foek6kzb">
            <span className="text-base font-semibold text-gray-900" data-id="element-3149" data-mp="2516sweo6t0f4">
              Net payout
            </span>
            <span className="text-lg font-bold tabular-nums text-gray-900" data-id="element-3150" data-mp="29tghnrdkfuft">
              {fmtAmount(payout.netAmount)} {payout.currency}
            </span>
          </div>
        </div>
      </div>
    </Section>;
}
// ── Right-rail Details ───────────────────────────────────────────────────
function DetailsRail({
  payout
}: {
  payout: Payout;
}) {
  // Operational reference only — no financial figures here. The Net payout
  // and its full breakdown live in the Payout calculation card so there's a
  // single source of truth for the numbers.
  const display = getDisplayStatus(payout);
  const createdAt = getInitiatedAt(payout);
  const isSubmitted = payout.status === 'submitted' || payout.status === 'partially_submitted';
  return <aside className="space-y-6" data-id="element-3151" data-mp="3uckbgw154u4x">
      <Section title="Details" data-id="element-3152" data-mp="vskc6dvh40bz">
        <dl className="px-6 py-4 space-y-4" data-id="element-3153" data-mp="vrirriski1hl">
          <DetailItem label="Payout ID" mono copyable={payout.payoutId} data-id="element-3154" data-mp="qtqzuorxafde">
            {payout.payoutId}
          </DetailItem>
          <DetailItem label="Status" data-id="element-3155" data-mp="m2inbjvaqdw0">
            <StatusPill status={display} data-id="element-3156" data-mp="m1g0po50axlw" />
          </DetailItem>
          <DetailItem label="Payout description" data-id="element-3157" data-mp="h4plt44xu822">{BRAND.payoutNote}</DetailItem>
          <DetailItem label="Created at" data-id="element-3158" data-mp="1ej1tznerpbik">{fmtDateTime(createdAt)}</DetailItem>
          <DetailItem label="Submitted at" data-id="element-3159" data-mp="1el67mx2gxow1">
            {isSubmitted ? fmtDateTime(payout.effectiveDateRaw) : '—'}
          </DetailItem>
          <DetailItem label="Currency" data-id="element-3160" data-mp="34072z1wndqdv">{payout.currency}</DetailItem>
          <DetailItem label="Destination account" data-id="element-3161" data-mp="38zk14isyyjvr">
            {payout.destinationAccount}
          </DetailItem>
        </dl>
      </Section>
    </aside>;
}
function DetailItem({
  label,
  children,
  mono,
  copyable
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div data-id="element-3162" data-mp="2l9poa18ngvbi">
      <dt className="text-xs font-medium text-gray-500 mb-1" data-id="element-3163" data-mp="2l8wdmp6kllo3">{label}</dt>
      <dd className="flex items-center gap-1.5 min-w-0" data-id="element-3164" data-mp="2pc2ibse1b4sg">
        <span className={`text-sm text-gray-900 min-w-0 truncate ${mono ? 'font-mono' : ''}`} data-id="element-3165" data-mp="2p9gbxw2vqvbt">
          {children}
        </span>
        {copyable && <CopyButton value={copyable} label={typeof label === 'string' ? label : 'value'} size={12} data-id="element-3166" data-mp="2gbnw8qt8xlnw" />}
      </dd>
    </div>;
}
// ── Transfers list ───────────────────────────────────────────────────────
function TransfersSection({
  payout
}: {
  payout: Payout;
}) {
  if (payout.transfers.length <= 1) return null;
  return <Section icon={ActivityIcon} title="Bank transfers" description={`This payout was sent to ${payout.destinationAccount} as ${payout.transfers.length} separate transfers.`} data-id="element-3167" data-mp="1u38r5jl42evp">
      <div className="px-6 pt-3 pb-4 overflow-x-auto" data-id="element-3168" data-mp="240dazcrhhy9c">
        <table className="w-full text-sm" data-id="element-3169" data-mp="23ysh7pczujcj">
          <thead data-id="element-3170" data-mp="2hr8nr053szi7">
            <tr className="text-xs text-gray-500 border-b border-gray-100" data-id="element-3171" data-mp="2hqqorvr2476l">
              <th className="text-left font-medium py-2 pr-4" data-id="element-3172" data-mp="2ctqdn6fcoe8m">Transfer ID</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3173" data-mp="2rpxx24ixpbmu">Status</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3174" data-mp="2rnva5td2vymc">
                <span className="inline-flex items-center" data-id="element-3175" data-mp="2msfwp2izg4qg">
                  <span data-id="element-3176" data-mp="xbwy7jndwomr">Payout transfer reference</span>
                  <InfoTooltip content="A reference used to identify this payout transfer. It may appear on your bank statement." label="What is the payout transfer reference?" placement="top" align="left" data-id="element-3177" data-mp="xcghp4sonl2w" />
                </span>
              </th>
              <th className="text-right font-medium py-2 pl-3" data-id="element-3178" data-mp="3pj7cr5g5pkqj">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-3179" data-mp="3tlu5d8knj9os">
            {payout.transfers.map(t => <tr key={t.transferId} className="align-top" data-id="element-3180" data-mp="2wvuomqucl4v4">
                <td className="py-3 pr-4" data-id="element-3181" data-mp="2wt8h2r7gipcw">
                  <div className="font-mono text-gray-900" data-id="element-3182" data-mp="31tcxkh8d7k34">{t.transferId}</div>
                  {t.description && <div className="text-xs text-gray-500 mt-0.5" data-id="element-3183" data-mp="35uyc070js6ff">
                      {t.description}
                    </div>}
                </td>
                <td className="py-3 px-3" data-id="element-3184" data-mp="2e53h7utx7ojd">
                  <TransferStatusBadge status={t.status} data-id="element-3185" data-mp="2e4boqsvme6xb" />
                </td>
                <td className="py-3 px-3" data-id="element-3186" data-mp="2i4ugkmxijczi">
                  <div className="flex items-center gap-1.5 min-w-0" data-id="element-3187" data-mp="2i3sxbv6cfk6p">
                    <span className="font-mono text-gray-700 truncate" data-id="element-3188" data-mp="2alv0aq5cm1kd">
                      {t.bankReference}
                    </span>
                    <CopyButton value={t.bankReference} label="payout transfer reference" size={12} data-id="element-3189" data-mp="21lps4dsbyz55" />
                  </div>
                </td>
                <td className="py-3 pl-3 text-right tabular-nums font-medium text-gray-900 whitespace-nowrap" data-id="element-3190" data-mp="1rotrsvcl5jbo">
                  {fmtAmount(t.netAmount)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </Section>;
}
function TransferStatusBadge({
  status
}: {
  status: 'submitted' | 'failed' | 'cancelled' | 'pending';
}) {
  const styles: Record<typeof status, string> = {
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
    pending: 'bg-gray-50 text-gray-700 border-gray-200'
  };
  const label = status === 'submitted' ? 'Submitted' : status === 'failed' ? 'Failed' : status === 'cancelled' ? 'Cancelled' : 'Pending';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${styles[status]}`} data-id="element-3191" data-mp="pbio5dbga60k">
      {label}
    </span>;
}
// ── Export: CSV helpers ────────────────────────────────────────────────────
const round2 = (n: number) => Math.round(n * 100) / 100;
function escCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadBlob(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
const csvDate = () => new Date().toISOString().slice(0, 10);
// Payout summary — payout-level reconciliation fields (one row).
function downloadPayoutSummaryCsv(payout: Payout, merchantName: string) {
  const headers = ['Payout ID', 'Merchant name', 'Status', 'Effective Date', 'Currency', 'Gross Amount', 'Net adjustments', 'Net payout', 'Destination Account', 'Bank payout reference', 'Number of transfers'];
  const row = [escCsv(payout.payoutId), escCsv(merchantName), escCsv(payout.status), escCsv(payout.effectiveDate), escCsv(payout.currency), payout.grossAmount.toFixed(2), payout.netAdjustments.toFixed(2), payout.netAmount.toFixed(2), escCsv(payout.destinationAccount), escCsv(getPayoutTraceId(payout)), payout.transfers.length].join(',');
  downloadBlob(`${BRAND.name.toLowerCase()}-payout-${payout.payoutId}-summary-${csvDate()}.csv`, '\uFEFF' + [headers.join(','), row].join('\n'));
}
// Transaction details — transaction-level rows. MDR / fixed fee detail lives
// here (deliberately kept off the main page). Deterministic per payout.
interface PayoutTxnRow {
  transactionId: string;
  orderReference: string;
  grossAmount: number;
  mdrRate: number;
  percentageFee: number;
  fixedFee: number;
  totalProcessingFee: number;
  vatOnFee: number;
  whtAdjustment: number;
  reserveWithheld: number;
  netTransactionAmount: number;
  amountIncludedInPayout: number;
  payoutStatus: string;
}
function generatePayoutTransactions(payout: Payout): PayoutTxnRow[] {
  const seed = payout.payoutId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = (i: number) => {
    const x = Math.sin(seed * 7 + i * 13) * 10000;
    return x - Math.floor(x);
  };
  const MDR = 0.02826;
  const FIXED = 0.3;
  const rows: PayoutTxnRow[] = [];
  const count = 8 + Math.floor(rng(0) * 8);
  let remainingGross = payout.grossAmount;
  const base = payout.grossAmount / count;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const gross = isLast ? round2(remainingGross) : round2(Math.max(100, base * (0.5 + rng(i + 10))));
    const actualGross = Math.min(gross, remainingGross);
    remainingGross = round2(remainingGross - actualGross);
    const percentageFee = -round2(actualGross * MDR);
    const fixedFee = -FIXED;
    const totalProcessingFee = round2(percentageFee + fixedFee);
    const vatOnFee = -round2(Math.abs(totalProcessingFee) * 0.07);
    const whtAdjustment = round2(actualGross * 0.000847);
    const reserved = rng(i + 30) > 0.7;
    const reserveWithheld = reserved ? -round2(actualGross * 0.2) : 0;
    const netTransactionAmount = round2(actualGross + totalProcessingFee + vatOnFee + whtAdjustment);
    const amountIncludedInPayout = round2(netTransactionAmount + reserveWithheld);
    rows.push({
      transactionId: `txn_${String(10000 + Math.floor(rng(i + 20) * 90000))}`,
      orderReference: `ord_${String(100000 + Math.floor(rng(i + 40) * 900000))}`,
      grossAmount: actualGross,
      mdrRate: MDR,
      percentageFee,
      fixedFee,
      totalProcessingFee,
      vatOnFee,
      whtAdjustment,
      reserveWithheld,
      netTransactionAmount,
      amountIncludedInPayout,
      payoutStatus: reserved ? 'Partially reserved' : 'Included in payout'
    });
  }
  return rows;
}
function downloadTransactionDetailsCsv(payout: Payout) {
  const headers = ['Transaction ID', 'Order reference', 'Gross transaction amount', 'MDR rate', 'Percentage fee', 'Fixed fee', 'Total processing fee', 'VAT on fee', 'WHT adjustment', 'Reserve withheld', 'Net transaction amount', 'Amount included in payout', 'Transaction payout status'];
  const rows = generatePayoutTransactions(payout).map(t => [escCsv(t.transactionId), escCsv(t.orderReference), t.grossAmount.toFixed(2), `${(t.mdrRate * 100).toFixed(3)}%`, t.percentageFee.toFixed(2), t.fixedFee.toFixed(2), t.totalProcessingFee.toFixed(2), t.vatOnFee.toFixed(2), t.whtAdjustment.toFixed(2), t.reserveWithheld.toFixed(2), t.netTransactionAmount.toFixed(2), t.amountIncludedInPayout.toFixed(2), escCsv(t.payoutStatus)].join(','));
  downloadBlob(`${BRAND.name.toLowerCase()}-payout-${payout.payoutId}-transactions-${csvDate()}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ── Export menu ──────────────────────────────────────────────────────────
function ExportMenu({
  payout
}: {
  payout: Payout;
}) {
  const [open, setOpen] = useState(false);
  const {
    selectedCompany
  } = useCompany();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative inline-block" data-id="element-3192" data-mp="3bhkw1suqstv7">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500" data-id="element-3193" data-mp="3bj5vnwvwz2zx">
        <DownloadIcon size={16} aria-hidden="true" data-id="element-3194" data-mp="3u7kie7z77uux" />
        Export
        <ChevronDownIcon size={14} aria-hidden="true" data-id="element-3195" data-mp="3paan4bp4ooz6" />
      </button>
      {open && <div role="menu" className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-3196" data-mp="80cktruiss9g">
          <button type="button" role="menuitem" onClick={() => {
        downloadPayoutSummaryCsv(payout, selectedCompany.name);
        setOpen(false);
      }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3197" data-mp="307znoio39cw">
            <FileTextIcon size={14} className="text-gray-400" aria-hidden="true" data-id="element-3198" data-mp="hnymat77lzhd" />
            <div data-id="element-3199" data-mp="2ykdbm7jpk2i">
              <div className="font-medium" data-id="element-3200" data-mp="2y0r3tav49io">Download payout summary</div>
              <div className="text-xs text-gray-500" data-id="element-3201" data-mp="7s54auaq1ds7">
                Payout-level reconciliation
              </div>
            </div>
          </button>
          <button type="button" role="menuitem" onClick={() => {
        downloadTransactionDetailsCsv(payout);
        setOpen(false);
      }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3202" data-mp="3v34ohpgoebzf">
            <FileTextIcon size={14} className="text-gray-400" aria-hidden="true" data-id="element-3203" data-mp="s9yjl1fugqq6" />
            <div data-id="element-3204" data-mp="ndzwria5zgrv">
              <div className="font-medium" data-id="element-3205" data-mp="il6sswmiscgu">Download transaction details</div>
              <div className="text-xs text-gray-500" data-id="element-3206" data-mp="ij3rvhjtwyxc">
                One row per transaction
              </div>
            </div>
          </button>
        </div>}
    </div>;
}
// ── Page ─────────────────────────────────────────────────────────────────
export function PayoutDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const payout = id ? getPayoutById(id) : undefined;
  if (!payout) {
    return <div className="max-w-4xl mx-auto py-16 text-center" data-id="element-3207" data-mp="3qtng61fgidjg">
        <p className="text-gray-600 mb-4" data-id="element-3208" data-mp="1u03kkea39frw">Payout not found.</p>
        <Link to="/payouts" className="text-sm font-medium text-yellow-700 hover:text-yellow-800" data-id="element-3209" data-mp="1tzkcdpa8q5r2">
          ← Back to Payouts
        </Link>
      </div>;
  }
  const display = getDisplayStatus(payout);
  // The subtitle carries the single canonical date for this payout, with
  // an explicit MVT timestamp so it's unambiguous and reconcilable. For
  // sent payouts we surface the *initiation* moment (which we control)
  // rather than a "funds arrived" prediction we don't.
  const subtitle = (() => {
    const eff = payout.effectiveDateRaw;
    switch (display) {
      case 'paid':
        return `Sent to ${payout.destinationAccount} · initiated ${fmtDateTimeMVT(getInitiatedAt(payout))}`;
      case 'scheduled':
        return `Scheduled for ${fmtDateTimeMVT(eff)}`;
      case 'failed':
        return `Failed on ${fmtDateTimeMVT(eff)}`;
      case 'partially_submitted':
        return `Partially submitted on ${fmtDateTimeMVT(eff)}`;
      case 'cancelled':
        return `Cancelled on ${fmtDateTimeMVT(eff)}`;
    }
  })();
  return <div className="max-w-[1280px] mx-auto pb-12" data-id="element-3210" data-mp="33xaa1qpko0mb">
      {/* Back link */}
      <button type="button" onClick={() => navigate('/payouts')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded" data-id="element-3211" data-mp="13vkjjo7xquji">
        <ArrowLeftIcon size={14} aria-hidden="true" data-id="element-3212" data-mp="18vgekxynnbmx" />
        Payouts
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4" data-id="element-3213" data-mp="l5ckbh7cwo8l">
        <div className="min-w-0" data-id="element-3214" data-mp="l3hx1w8m4k1c">
          <div className="text-xs font-semibold uppercase tracking-wider text-yellow-700 mb-2" data-id="element-3215" data-mp="g71c1awpmlsh">
            Payouts
          </div>
          <div className="flex items-baseline gap-3 flex-wrap mb-2" data-id="element-3216" data-mp="typl15bnwu58">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900" data-id="element-3217" data-mp="p6o77eyuivd3">
              {fmtAmount(payout.netAmount)}
            </h1>
            <span className="text-xl text-gray-500 font-medium" data-id="element-3218" data-mp="1g24m37m23ah">
              {payout.currency}
            </span>
            <StatusPill status={display} data-id="element-3219" data-mp="3tjbgaiubw9m3" />
          </div>
          <p className="text-sm text-gray-600" data-id="element-3220" data-mp="3jgfw5iewghhq">{subtitle}</p>
        </div>
        <div className="flex-shrink-0" data-id="element-3221" data-mp="3o7xq3mii9ht7">
          <ExportMenu payout={payout} data-id="element-3222" data-mp="3o9qusw9k5ryk" />
        </div>
      </div>

      {/* Body: 2-col on desktop, stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-3223" data-mp="39h6v65lma2lv">
        <div className="space-y-6 min-w-0" data-id="element-3224" data-mp="3ej61nocnlfn1">
          <AccountDetails payout={payout} data-id="element-3225" data-mp="62kywpam7isd" />

          <PayoutCalculationSection payout={payout} data-id="element-3226" data-mp="63mbiy7a4dc7" />

          <TransfersSection payout={payout} data-id="element-3227" data-mp="b1nrha9erwjg" />
        </div>

        <DetailsRail payout={payout} data-id="element-3228" data-mp="b15ozvw5km33" />
      </div>
    </div>;
}